import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import BaseballDiamond from '@/components/BaseballDiamond';
import Scoreboard from '@/components/Scoreboard';
import Baseball from '@/components/Baseball';
import Fireworks from '@/components/Fireworks';
import RunScoredNotification from '@/components/RunScoredNotification';
import { toast } from 'sonner';
import { playClick, playToggle, playSuccess, playHit, playOut, playHomeRun } from '@/utils/sounds';
import { AvatarCanvasSmall } from '@/components/AvatarCanvas';
import { RetroBackground, RetroButton, pixelStripes, PALETTE, ACCENTS, DESIGN_TOKENS } from '@/components/RetroUI';

export default function GamePlay() {
  const navigate = useNavigate();
  const {
    gameStarted,
    gameEnded,
    teamA,
    teamB,
    currentInning,
    totalInnings,
    isTopInning,
    battingTeam,
    pitchingTeam,
    outs,
    bases,
    getCurrentBatter,
    getRandomSong,
    recordAtBat,
    checkEndOfHalfInning,
    endHalfInning,
    linkedAccounts,
  } = useGame();

  const [phase, setPhase] = useState('pitch'); // 'pitch', 'playing', 'result'
  const [playProgress, setPlayProgress] = useState(0);
  const [song, setSong] = useState(null);
  const [guesses, setGuesses] = useState({ title: false, artist: false, album: false, year: false });
  const [lastResult, setLastResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [isPlaying, setIsPlaying] = useState(false);
  const [retryTrigger, setRetryTrigger] = useState(0); // Used to trigger retry
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const startTimeRef = useRef(null);
  const audioRetryCountRef = useRef(0);
  const guessesRef = useRef(guesses); // Ref to access current guesses in timer callback
  const maxAudioRetries = 3;

  // Keep guessesRef in sync with guesses state
  useEffect(() => {
    guessesRef.current = guesses;
  }, [guesses]);

  // Reset scroll on phase change so the scoreboard stays in view
  // (result view is shorter than the pitch view; stale scroll hid the scoreboard)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [phase]);

  const batter = getCurrentBatter();
  const battingTeamData = battingTeam === 'A' ? teamA : teamB;
  const pitchingTeamData = pitchingTeam === 'A' ? teamA : teamB;
  const batterAvatarConfig = linkedAccounts?.[batter.id]?.avatar_config;

  useEffect(() => {
    if (!gameStarted) {
      navigate('/dashboard');
    }
  }, [gameStarted, navigate]);

  useEffect(() => {
    if (gameEnded) {
      navigate('/end-game');
    }
  }, [gameEnded, navigate]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  }, []);

  // Trigger a retry when retryTrigger changes (used for audio failure retries)
  useEffect(() => {
    if (retryTrigger > 0) {
      // Trigger a new pitch attempt
      const pitchButton = document.querySelector('[data-testid="pitch-btn"]');
      if (pitchButton) {
        pitchButton.click();
      }
    }
  }, [retryTrigger]);

  const handlePitch = useCallback(async () => {
    playSuccess(); // Play sound when pitch starts
    // Clear any existing timer before starting
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopAudio();
    
    // Try to find a song with a valid preview URL (max 10 attempts)
    let newSong = null;
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      newSong = getRandomSong();
      
      if (!newSong) {
        toast.error('No more songs available!');
        navigate('/end-game');
        return;
      }
      
      // Check if song has a preview URL
      if (newSong.preview_url) {
        break; // Found a valid song
      }
      
      // Song has no preview, try another
      console.log(`Song "${newSong.title}" has no preview, trying another...`);
      attempts++;
      newSong = null;
    }
    
    if (!newSong) {
      toast.error('Could not find a song with audio preview. Try a different playlist.');
      navigate('/dashboard');
      return;
    }
    
    setSong(newSong);
    setPhase('playing');
    setPlayProgress(0);
    setTimeRemaining(30);
    
    // Start 30 second timer first (so game can continue even if audio fails)
    startTimeRef.current = Date.now();
    const duration = 30000; // 30 seconds
    
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min((elapsed / duration) * 100, 100);
      const remaining = Math.max(Math.ceil((duration - elapsed) / 1000), 0);
      
      setPlayProgress(progress);
      setTimeRemaining(remaining);
      
      if (elapsed >= duration) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        // Timer ended but pitch stays active - user must click "End Pitch" to submit
        // No auto-submit anymore
      }
    }, 100);
    
    // Play audio using HTML5 audio element
    if (audioRef.current && newSong.preview_url) {
      audioRef.current.src = newSong.preview_url;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          audioRetryCountRef.current = 0; // Reset retry count on success
        })
        .catch((err) => {
          console.error('Audio play error:', err);
          audioRetryCountRef.current++;
          
          if (audioRetryCountRef.current < maxAudioRetries) {
            toast.error(`Audio failed. Trying another song... (${audioRetryCountRef.current}/${maxAudioRetries})`);
            // Clear timer and trigger retry via state change
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setPhase('pitch');
            setRetryTrigger(prev => prev + 1);
          } else {
            // Max retries reached - continue without audio
            toast.error('Audio playback unavailable. Continue guessing based on the reveal!');
            audioRetryCountRef.current = 0;
            // Don't retry, let the timer run so game can continue
          }
        });
    }
  }, [getRandomSong, navigate, stopAudio, recordAtBat]);

  const handleEndPitch = async () => {
    playClick();
    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Stop audio
    stopAudio();
    // Record the score with current guesses (now async - calls backend)
    const correctCount = Object.values(guesses).filter(Boolean).length;
    const result = await recordAtBat(correctCount);
    
    // Show error toast if API call failed
    if (result.result === 'ERROR') {
      toast.error('Failed to process at-bat. Please try again or restart the game.');
    } else if (result.result === 'OUT') {
      playOut();
    } else if (result.result === 'HOME RUN') {
      playHomeRun();
    } else {
      playHit();
    }
    
    // Snapshot batter (index advances during recordAtBat)
    setLastResult({ ...result, batterName: batter.name, batterId: batter.id });
    setPhase('result');
  };

  const handleNextBatter = () => {
    playClick();
    // Check if half inning is over
    if (checkEndOfHalfInning()) {
      endHalfInning();
      navigate('/inning-summary');
    } else {
      // Reset for next batter
      setGuesses({ title: false, artist: false, album: false, year: false });
      setSong(null);
      setLastResult(null);
      setPhase('pitch');
    }
  };

  const toggleGuess = (field) => {
    playToggle();
    setGuesses(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Cleanup timer and audio on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      stopAudio();
    };
  }, [stopAudio]);

  // Handle audio ending early (some previews are shorter than 30s)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => {
        // Audio ended naturally, but keep timer running for full 30s
        setIsPlaying(false);
      };
      
      const handleError = (e) => {
        console.error('Audio error:', e);
        setIsPlaying(false);
        // Continue the game even if audio fails - the 30s timer will still run
        toast.error('Audio failed to load, but you can still play!');
      };
      
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      return () => {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    }
  }, []);

  const getResultEmoji = (result) => {
    switch (result) {
      case 'SINGLE': return '1B';
      case 'DOUBLE': return '2B';
      case 'TRIPLE': return '3B';
      case 'HOME RUN': return 'HR';
      case 'OUT': return 'OUT';
      default: return '';
    }
  };

  return (
    <RetroBackground variant="grass" className="min-h-screen p-2">
      <div className="max-w-md mx-auto">
        {/* Scoreboard Header */}
        <Scoreboard 
          teamA={teamA}
          teamB={teamB}
          currentInning={currentInning}
          totalInnings={totalInnings}
          isTopInning={isTopInning}
          outs={outs}
        />

        {/* Baseball Diamond - smaller on mobile */}
        <div className="flex justify-center my-2 md:my-6">
          <BaseballDiamond bases={bases} />
        </div>

        {/* Game Info - black LED bar per reference */}
        <div 
          className="mb-2 md:mb-4 px-3 py-2"
          style={{
            background: PALETTE.scoreboardBg,
            border: `3px solid ${PALETTE.ink}`,
            borderRadius: DESIGN_TOKENS.borderRadius.md,
            boxShadow: `4px 4px 0px rgba(0,0,0,0.35)`,
          }}
        >
          <div className="flex justify-between items-center">
            <span style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '11px',
              color: '#FFFFFF',
              letterSpacing: '1px',
            }}>
              {isTopInning ? 'TOP' : 'BOT'} {currentInning}
            </span>
            <span className="flex items-center gap-2" style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 600,
              fontSize: '13px',
              color: ACCENTS.gold,
            }}>
              {batterAvatarConfig && (
                <span data-testid="batting-bar-avatar" className="inline-flex">
                  <AvatarCanvasSmall config={batterAvatarConfig} size={24} />
                </span>
              )}
              {battingTeamData.name} BATTING
            </span>
          </div>
        </div>

        {/* Pitch Phase */}
        {phase === 'pitch' && (
          <div 
            className="text-center p-4"
            style={{
              background: PALETTE.cream,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
              boxShadow: `6px 6px 0px ${PALETTE.ink}`,
            }}
            data-testid="pitch-phase"
          >
            {/* Pitching Team - striped HOLD THE PHONE panel per reference */}
            <div 
              className="p-3 mb-4"
              style={{
                backgroundColor: '#C6BBA5',
                backgroundImage: pixelStripes('C6BBA5', 'B6AA92'),
                backgroundSize: '48px 48px',
                border: `3px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.md,
              }}
            >
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '13px',
                color: PALETTE.ink,
                marginBottom: '6px',
                letterSpacing: '1px',
              }}>
                {pitchingTeamData.name.toUpperCase()}
              </p>
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontWeight: 700,
                fontSize: '13px',
                color: PALETTE.ink,
              }}>
                ♪ YOU ARE PITCHING - HOLD THE PHONE ♪
              </p>
            </div>
            
            <div className="flex justify-center mb-4">
              <Baseball size={50} spinning={false} />
            </div>
            
            {/* Now Batting - black bar with avatar chip per reference */}
            <div 
              className="p-3 mb-4"
              style={{
                background: PALETTE.scoreboardBg,
                border: `3px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.md,
              }}
            >
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '9px',
                color: '#FFFFFF',
                marginBottom: '8px',
                letterSpacing: '2px',
              }}>
                NOW BATTING
              </p>
              <div className="flex items-center justify-center gap-2">
                {batterAvatarConfig && (
                  <span data-testid="now-batting-avatar" className="inline-flex">
                    <AvatarCanvasSmall config={batterAvatarConfig} size={32} />
                  </span>
                )}
                <p 
                  style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontWeight: 700,
                    fontSize: '16px',
                    color: ACCENTS.gold,
                  }}
                  data-testid="current-batter"
                >
                  {batter.name}
                </p>
              </div>
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '11px',
                color: `${PALETTE.cream}99`,
                marginTop: '4px',
              }}>
                ({battingTeamData.name})
              </p>
            </div>
            
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 600,
              fontSize: '13px',
              color: PALETTE.ink,
              marginBottom: '12px',
            }}>
              Press PITCH when {batter.name} is ready!
            </p>
            
            <RetroButton
              variant="primary"
              size="md"
              onClick={handlePitch}
              data-testid="pitch-btn"
            >
              PITCH!
            </RetroButton>
          </div>
        )}

        {/* Playing Phase - Now includes answer buttons */}
        {phase === 'playing' && song && (
          <div 
            className="p-3"
            style={{
              background: PALETTE.cream,
              border: `4px solid ${PALETTE.ink}`,
              boxShadow: `6px 6px 0px ${PALETTE.ink}`,
            }}
            data-testid="playing-phase"
          >
            {/* Header with pitcher info and timer */}
            <div 
              className="flex items-center justify-between mb-3 pb-2"
              style={{ borderBottom: `2px solid ${PALETTE.dirt}33` }}
            >
              <div>
                <p style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '8px',
                  color: PALETTE.dirt,
                }}>
                  {pitchingTeamData.name} PITCHING
                </p>
                <p style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '11px',
                  color: PALETTE.grass,
                }}>
                  {batter.name} batting
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '10px',
                  color: PALETTE.dirt,
                }}>
                  {isPlaying ? '♪' : ''}
                </span>
                <span style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '18px',
                  color: PALETTE.scoreboardText,
                  textShadow: `0 0 8px ${PALETTE.scoreboardText}`,
                  background: PALETTE.scoreboardBg,
                  padding: '4px 8px',
                }}>
                  {timeRemaining}s
                </span>
              </div>
            </div>
            
            {/* Audio Playhead */}
            <div 
              className="mb-3"
              style={{
                height: '20px',
                background: PALETTE.scoreboardBg,
                border: `2px solid ${PALETTE.ink}`,
              }}
              data-testid="audio-playhead"
            >
              <div 
                style={{ 
                  width: `${playProgress}%`,
                  height: '100%',
                  background: `linear-gradient(180deg, ${PALETTE.grass} 0%, #3d7330 100%)`,
                  transition: 'width 0.1s linear',
                }}
              ></div>
            </div>
            
            {/* Instruction */}
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '8px',
              color: PALETTE.dirt,
              textAlign: 'center',
              marginBottom: '8px',
            }}>
              {timeRemaining > 0 
                ? `Tap answers ${batter.name} gets correct:`
                : `Audio ended - select answers then END PITCH`
              }
            </p>
            
            {/* Answer Buttons */}
            <div className="space-y-2 mb-3">
              {[
                { key: 'title', label: 'TITLE', value: song.title },
                { key: 'artist', label: 'ARTIST', value: song.artist },
                { key: 'album', label: 'ALBUM', value: song.album },
                { key: 'year', label: 'YEAR', value: song.year },
              ].map((item) => (
                <div 
                  key={item.key}
                  className="p-2 cursor-pointer transition-all"
                  style={{
                    border: `4px solid ${guesses[item.key] ? PALETTE.grass : PALETTE.ink}`,
                    background: guesses[item.key] ? PALETTE.grass : '#FFFFFF',
                    color: guesses[item.key] ? PALETTE.cream : PALETTE.ink,
                  }}
                  onClick={() => toggleGuess(item.key)}
                  data-testid={`guess-${item.key}`}
                >
                  <span style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '8px',
                    display: 'block',
                    opacity: 0.75,
                  }}>
                    {item.label}
                  </span>
                  <span style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '12px',
                    lineHeight: 1.3,
                  }}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
            
            {/* End Pitch Button */}
            <RetroButton
              variant="primary"
              size="md"
              fullWidth
              onClick={handleEndPitch}
              data-testid="end-pitch-btn"
            >
              END PITCH
            </RetroButton>
          </div>
        )}

        {/* Result Phase */}
        {phase === 'result' && lastResult && (
          <div 
            className="text-center p-4 relative z-10"
            style={{
              background: PALETTE.cream,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
              boxShadow: `6px 6px 0px ${PALETTE.ink}`,
            }}
            data-testid="result-phase"
          >
            {/* Fireworks for Home Run - renders behind due to z-index */}
            {lastResult.result === 'HOME RUN' && <Fireworks />}
            
            {/* Run Scored Notification */}
            {lastResult.runsScored > 0 && (
              <RunScoredNotification 
                runs={lastResult.runsScored} 
                teamName={battingTeamData.name} 
              />
            )}
            
            <h2 style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: DESIGN_TOKENS.fontSizes.displaySm,
              color: PALETTE.ink,
              marginBottom: '8px',
              textShadow: `2px 2px 0px rgba(0,0,0,0.15)`,
              letterSpacing: '2px',
            }}>
              RESULT
            </h2>
            
            {/* Batter chip - who just batted */}
            {lastResult.batterName && (
              <div className="flex items-center justify-center gap-2 mb-3">
                {linkedAccounts?.[lastResult.batterId]?.avatar_config && (
                  <span data-testid="result-avatar" className="inline-flex">
                    <AvatarCanvasSmall 
                      config={linkedAccounts[lastResult.batterId].avatar_config} 
                      size={28} 
                    />
                  </span>
                )}
                <span 
                  style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontWeight: 700,
                    fontSize: '14px',
                    color: PALETTE.dirt,
                  }}
                  data-testid="result-batter-name"
                >
                  {lastResult.batterName}
                </span>
              </div>
            )}
            
            {/* Black LED result panel with giant yellow letters per reference */}
            <div 
              className="mb-4"
              style={{
                background: PALETTE.scoreboardBg,
                border: `4px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.md,
                padding: '20px 16px',
              }}
            >
              <div 
                style={{
                  fontFamily: DESIGN_TOKENS.fonts.display,
                  fontSize: '44px',
                  lineHeight: 1,
                  color: lastResult.result === 'OUT' ? '#FF3B3B' : ACCENTS.gold,
                  textShadow: lastResult.result === 'OUT'
                    ? `0 0 14px rgba(255,59,59,0.6)`
                    : `0 0 14px rgba(242,196,56,0.6)`,
                  marginBottom: '14px',
                }}
              >
                {getResultEmoji(lastResult.result)}
              </div>
              
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: DESIGN_TOKENS.fontSizes.displaySm,
                color: '#FFFFFF',
                letterSpacing: '2px',
                marginBottom: lastResult.runsScored > 0 ? '10px' : 0,
              }}>
                {lastResult.result === 'OUT' ? 'BETTER LUCK NEXT TIME' : lastResult.result}
              </p>
              
              {lastResult.runsScored > 0 && (
                <p style={{
                  fontFamily: DESIGN_TOKENS.fonts.display,
                  fontSize: '13px',
                  color: PALETTE.scoreboardText,
                  textShadow: `0 0 10px ${PALETTE.scoreboardText}`,
                  letterSpacing: '1px',
                }}>
                  +{lastResult.runsScored} RUN{lastResult.runsScored > 1 ? 'S' : ''}!
                </p>
              )}
            </div>
            
            <RetroButton
              variant="primary"
              size="md"
              onClick={handleNextBatter}
              data-testid="next-batter-btn"
            >
              {checkEndOfHalfInning() ? 'END OF INNING' : 'NEXT BATTER'}
            </RetroButton>
          </div>
        )}

        {/* Hidden audio element for preview playback */}
        <audio ref={audioRef} style={{ display: 'none' }} crossOrigin="anonymous" />
      </div>
    </RetroBackground>
  );
}
